import { Link } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import NavHeader from '@/components/NavHeader';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Copyright } from 'lucide-react';

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="mb-6" style={{ background: 'hsl(30 6% 7%)', borderColor: 'hsl(35 12% 14%)' }}>
    <CardContent className="p-8">
      <h2
        className="text-2xl font-semibold mb-4"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: 'hsl(38 65% 57%)',
          letterSpacing: '0.02em',
        }}
      >
        {title}
      </h2>
      <div className="text-sm leading-relaxed space-y-3" style={{ color: 'hsl(40 15% 72%)' }}>
        {children}
      </div>
    </CardContent>
  </Card>
);

export default function CopyrightPage() {
  return (
    <PageLayout>
      <NavHeader />
      <div className="container py-24 max-w-3xl">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm mb-10 transition-colors hover:opacity-80"
          style={{ color: 'hsl(38 65% 57%)' }}
        >
          <ArrowLeft size={15} />
          Back to SelfWorth DApp
        </Link>

        {/* Header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Copyright size={22} style={{ color: 'hsl(38 65% 57%)' }} />
            <span
              className="text-sm font-medium tracking-widest uppercase"
              style={{ color: 'hsl(35 8% 52%)' }}
            >
              Legal
            </span>
          </div>
          <h1
            className="text-5xl font-semibold mb-3"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: 'hsl(40 15% 92%)',
              lineHeight: 1.15,
            }}
          >
            Copyright Notice
          </h1>
          <p className="text-sm" style={{ color: 'hsl(35 8% 52%)' }}>
            SelfWorth DApp
          </p>
        </div>

        {/* Copyright statement card — prominent */}
        <Card
          className="mb-8"
          style={{
            background: 'linear-gradient(135deg, hsl(30 6% 7%) 0%, hsl(30 6% 9%) 100%)',
            borderColor: 'hsl(38 65% 57% / 0.3)',
          }}
        >
          <CardContent className="p-10 text-center">
            <div
              className="text-6xl mb-4"
              style={{ color: 'hsl(38 65% 57%)', fontFamily: "'Cormorant Garamond', serif" }}
            >
              ©
            </div>
            <p
              className="text-3xl font-semibold mb-2"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: 'hsl(40 15% 92%)',
              }}
            >
              2026 SelfWorth DApp
            </p>
            <p
              className="text-base tracking-widest uppercase font-medium"
              style={{ color: 'hsl(38 65% 57%)', letterSpacing: '0.15em' }}
            >
              All Rights Reserved
            </p>
          </CardContent>
        </Card>

        {/* Sections */}
        <SectionCard title="Ownership Statement">
          <p>
            SelfWorth DApp, including its name, branding, user interface, visual design, written
            content, and source code (collectively, the "Work"), is owned by and copyright of the
            SelfWorth DApp development team ("Owner"). The Work is protected under the copyright
            laws of applicable jurisdictions and international copyright treaties.
          </p>
          <p>
            No portion of the Work may be reproduced, distributed, publicly displayed, or
            incorporated into derivative works without the express prior written permission of the
            Owner, except as explicitly permitted under the terms below.
          </p>
        </SectionCard>

        <SectionCard title="Permitted Use">
          <p>
            The following uses are permitted without prior written consent, provided attribution to
            SelfWorth DApp is clearly included:
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Personal, non-commercial use</strong> —
              You may access and use the App for your own personal, non-commercial purposes as an
              end user.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Screenshots and media</strong> — You may
              capture screenshots or record the App interface for personal reference, educational
              discussion, or non-commercial journalism, provided the content is presented accurately
              and not used to misrepresent the App.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Linking</strong> — You may link to
              SelfWorth DApp's public URLs from your own website or social media, provided the link
              is not presented in a way that falsely implies endorsement or partnership.
            </li>
            <li>
              <strong style={{ color: 'hsl(40 15% 88%)' }}>Open-source components</strong> — Third-
              party open-source libraries incorporated in the App remain subject to their own
              respective licenses, which are unaffected by this notice.
            </li>
          </ul>
          <p>
            All other uses — including commercial reproduction, redistribution, white-labeling, or
            creation of competing services based on the Work — require prior written authorization
            from the Owner.
          </p>
        </SectionCard>

        <SectionCard title="Trademarks">
          <p>
            "SelfWorth DApp" and associated logos, wordmarks, and stylistic elements are
            proprietary marks of the Owner. Their use in any commercial context, or in any way that
            implies affiliation with or endorsement by SelfWorth DApp, is prohibited without
            express written consent.
          </p>
          <p>
            References to SelfWorth DApp in factual, non-commercial, or nominative contexts (such
            as reviews, comparisons, or news reporting) are permitted when accurate and not
            misleading.
          </p>
        </SectionCard>

        <SectionCard title="Digital Millennium Copyright Act (DMCA)">
          <p>
            If you believe that content available through SelfWorth DApp infringes your copyright,
            you may submit a notice of claimed infringement. Your notice should include a description
            of the copyrighted work, the location of the allegedly infringing material, your contact
            information, a statement of good-faith belief, and your signature.
          </p>
          <p>
            We will investigate all good-faith notices and, where appropriate, remove or disable
            access to infringing material in a timely manner. Counter-notices may be submitted
            according to applicable DMCA procedures.
          </p>
        </SectionCard>

        {/* Footer note */}
        <div
          className="mt-8 pt-6 text-xs"
          style={{ borderTop: '1px solid hsl(35 12% 14%)', color: 'hsl(35 8% 42%)' }}
        >
          <p>
            Copyright infringement is a serious matter. Unauthorized use of SelfWorth DApp's
            intellectual property may result in civil and criminal penalties under applicable law.
            For licensing inquiries or to report infringement, reach out through our official
            community channels.
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
